'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { formatNumberWithSuffix } from 'src/utils/formatNumberWithSuffix'
import { useSoundEffect } from 'src/hooks/useSoundEffect'
import { useSoundActions } from 'src/store/sound'
import { useDeviceInfo } from 'src/hooks/useDeviceInfo'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage, useGetGameBetConfig } from 'src/store/game'
import { useGetToast, useUIActions } from 'src/store/ui'
import type { MarbleGame } from 'src/types/domain/Game'
import type { AuthenticatedPlayer } from 'src/types/domain/Player'
import { fromInteger } from '@repo/money'
import { getCrashDisplayPrecision } from './formatMoney'
import { resolvePayoutCeiling } from './crashPayoutCeiling'
import { useCrashChipBetting } from './useCrashChipBetting'
import { useCrashNoticeDisplay } from './useCrashNotice'
import {
  getBeaconTime,
  getDisplayMultiplier,
  setBeaconRound,
  useFrameBeacon
} from './useFrameBeacon'
import { isBeaconDead } from './crashFrameBeacon'
import { resolveCountdownCue } from './crashCountdownCue'
import {
  DESKTOP_GAIN_PROFILE,
  MOBILE_GAIN_PROFILE,
  resolveCountdownGain
} from './crashCountdownGain'
import { getRaceCountdownInfo } from './raceCountdown'
import { hardCloseClock, hardCountdown, perfNow } from 'src/utils/hardCloseClock'
import { getBettingTimeWindow } from 'src/utils/getBettingTimeWindow'
import { getSettledTimeWindow } from 'src/utils/getSettledTimeWindow'
import { useVisualCrash } from './useVisualCrash'
import { crashEventClockOriginMs } from './crashHold'
import { CrashBetZone } from './CrashBetZone'
import { CrashZoneWinBanner } from './CrashZoneWinBanner'
import { CrashChipStrip } from './CrashChipStrip'
import { chipStyleForIndex } from './chipStack'
import {
  isZoneCashoutLocked,
  resolvePanelMode,
  resolveStripResting,
  resolveZoneCardState,
  resolveZoneRiding,
  resolvePotentialWin,
  resolveBettingPreviewWin,
  resolveZoneWinBanner,
  shouldPlayBustThump,
  totalInPlay,
  type PotentialWinReadout,
  type Zone
} from './crashChipLogic'
import './crash-chip-panel.scss'

// Stats the panel reports up to the page — rendered in the bottom-left
// chip-stats overlay (the same surface chip-placement games use; the
// top-right header balance is hidden for crash, see BalanceContainer).
export interface CrashPanelStats {
  // Placed stakes during betting; queued next-round stakes during live.
  totalStaked: number
  // Submitted and still riding in the current round.
  inPlay: number
  // The player's own potential-win story (the overlay's third pill): idle
  // ("—"), riding (locked payout + active stake, multiplied per-frame by the
  // pill itself), or won (all resolved, something banked).
  potential: PotentialWinReadout
}

type Props = Readonly<{
  game: MarbleGame
  player: AuthenticatedPlayer
  onStatsChange?: (stats: CrashPanelStats) => void
}>

interface FlyingChip {
  id: string
  fromX: number
  fromY: number
  toX: number
  toY: number
  bg: string
  border: string
  amount: number
}

const FLYING_CHIP_LIFETIME_MS = 350
const RECEIVE_PULSE_MS = 500
const DOUBLE_PUNCH_MS = 450

/**
 * Container for the crash chip-placement panel — visually a sibling of
 * `ChipPlacementPanel`: a transparent column of floating glass cards (no
 * monolithic dock chrome) and the bottom-left stats overlay (rendered by
 * the page). Three presentation modes (`resolvePanelMode`):
 *
 *   - betting:        two staking zones + auto rows + chip strip. When the
 *                     submission fires the staked zones LOCK (chip stack +
 *                     amount stay, placing → confirmed pulse) — no empty
 *                     void — then the SAME card morphs into the cash-out
 *                     treatment at the climb (v6: one card geometry).
 *   - live-collapsed: the player has bets riding → the staked zone cards
 *                     stay PUT; the chip strip RESTS in place (faded,
 *                     space-reserving — the panel is bottom-anchored and
 *                     unmounting would shift the cards) while unstaked
 *                     zones stay LIVE next-round builders (v12.1: no
 *                     ghosting). The footage dominates; the cards never
 *                     move.
 *   - live-full:      no bets riding → the normal full betting panel;
 *                     builder cards wear the in-card amber NEXT ROUND
 *                     eyebrow (v12.1 — the floating pill is gone), chips
 *                     are immediately usable, stakes queue and auto-submit
 *                     at the next round start (the hook's single-shot
 *                     pipeline).
 *
 * The WIDGET owns the multiplier + status text (`CrashMultiplierDisplay`,
 * mounted over the video in the page, alongside the geometry-only
 * `CrashHudOverlay`). This panel owns the betting surface.
 */
export function CrashChipPanel({ game, player, onStatsChange }: Props) {
  const { t } = useClientSideTranslation(useGetCurrentLanguage())
  const betConfig = useGetGameBetConfig(player)

  // Operator liability ceiling, in the DECIMAL units this panel renders in.
  // The crash multiplier is uncapped but the payout is not — the backend clamps
  // every cash-out to this — so the riding projections freeze here rather than
  // showing a figure the server will refuse to pay. maxPayout arrives in INTEGER
  // wire units; resolvePayoutCeiling owns that conversion (and maps 0/absent to
  // null = no ceiling, matching the backend's own `maxPayout > 0` guard).
  const payoutCeiling = useMemo(
    () =>
      resolvePayoutCeiling(betConfig?.maxPayout, (value) =>
        fromInteger(value, player.balance.currency)
      ),
    [betConfig?.maxPayout, player.balance.currency]
  )
  const { playCrashBust, playEffect } = useSoundEffect()
  const soundActions = useSoundActions()
  const { isMobile, isTablet } = useDeviceInfo()

  // NOTE (v6→v8): the provably-fair pill is GONE from the panel — the story
  // lives on the past-round pills (ShowRaceResults' crash variant, right
  // side → CrashRoundModal), Aviator-style: tap a past round's pill for
  // its commitment + reveal.

  // Origin element for the flying chip = the currently selected chip disc.
  const chipStripRef = useRef<HTMLDivElement>(null)

  const [flyingChips, setFlyingChips] = useState<FlyingChip[]>([])
  const [receivingZone, setReceivingZone] = useState<Zone | null>(null)
  const receiveClearRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [doublePunchAt, setDoublePunchAt] = useState(0)

  // Hooks must run before any early return — the guard below only affects
  // rendering. v47: a REAL fallback shape instead of the old `betConfig!`
  // assertion — during a currency switch the store selector goes
  // transiently undefined on the exact render where the hook still runs
  // (before the `return null` guard), and the hook dereferences
  // `betConfig.limits` at init → TypeError crash. The placeholder renders
  // nothing (the panel returns null right below) and is never bet against.
  const betting = useCrashChipBetting(
    game,
    player,
    betConfig ?? {
      probabilities: {},
      equalOdds: false,
      currency: player.balance.currency,
      maxBetAmount: 0,
      minBetAmount: 0,
      defaultBetMultiplier: 1,
      chipMultipliers: [],
      maxMultiplier: 0,
      maxPayout: 0,
      limits: { PickWinner: { min: 0, max: 0 } }
    }
  )

  // THE visual-crash signal — the whole panel (and, via the module store, the
  // status pill + HUD) flips its crash visuals on this, not on event arrival.
  // The beacon sampler is a refcounted singleton, so mounting the hook here
  // next to CrashMultiplierDisplay's mount still runs ONE 8Hz pipeline.
  const { getBeaconTime } = useFrameBeacon()
  const visualCrashed = useVisualCrash(game, getBeaconTime)

  // Round-scope the beacon (bug a) — redundant with CrashMultiplierDisplay's
  // identical call (setBeaconRound is idempotent), but keeps the round bound
  // in force when the hero display isn't mounted (spectator layouts).
  // The same origin the display passes: race_started's stamp, moved back by
  // 3 − R on a short-hold round (crashHold.ts).
  const beaconRace = game.races[0]
  const beaconStartedEvent = beaconRace?.raceEvents.find((e) => e.eventType === 'race_started')
  const beaconStartTs = crashEventClockOriginMs(beaconRace?.raceEvents ?? [])
  const beaconRoundId = beaconRace?.uuid
  useEffect(() => {
    setBeaconRound(beaconRoundId, beaconStartTs)
  }, [beaconRoundId, beaconStartTs])

  const {
    chips,
    selectedChipIdx,
    selectChip,
    stakes,
    placeChip,
    double,
    clear,
    clearZone,
    repeat,
    canControl,
    hasRepeatable,
    zoneAuto,
    setZoneAuto,
    myBet,
    cashout,
    cashingOut,
    optCashout,
    liveMultiplier,
    hasStartedClimbing,
    isBettingOpen,
    hasCrashed,
    crashedMultiplier,
    totalStaked,
    currency,
    inputPrecision,
    submittedStakes,
    submittedCashout,
    notice,
    fireNotice,
    myBetUnaccepted,
    stageTwo
  } = betting

  // ── Transient notice pill (v12.4 ask 7) ────────────────────────────────
  // ONE slot above the panel: the hook's abstract notices (placement
  // refusals, submit trims, aborts, cash-out failures) + the global
  // ui-store toast bridge (the bet:rejected reason channel that other
  // games render through PlaceBetPanel's Toast — crash pages had no
  // renderer). Mounting the row grows the bottom-anchored column UPWARD,
  // so zones and strip never shift (G13 fixed-geometry rule).
  const uiToast = useGetToast()
  const uiActions = useUIActions()
  const transientNotice = useCrashNoticeDisplay(notice, uiToast, uiActions.clearToast, t)
  // Stage 2 of a hard round (DESIGN §6) rests in the same slot, under any
  // transient notice: the lock's send until its answers come.
  const noticeDisplay =
    transientNotice ??
    (stageTwo === null
      ? null
      : {
          key: `stage-two:${stageTwo}`,
          tone: 'info' as const,
          text:
            stageTwo === 'placing'
              ? `${t('Locked')} · ${t('Placing bet...')}`
              : t('Confirming with your casino')
        })

  // ── Panel mode (chip-placement collapse model) ─────────────────────────
  // v12: riding is the pure resolveZoneRiding fact — a zone rides while a
  // NON-TERMINAL bet exists (or its submission fired and the id hasn't
  // landed yet). Cancelled AND cashed (won/cashed_out) bets free the zone:
  // the cashed side converts to the next-round builder (+ win banner) the
  // instant the cashout lands. Both-cashed → riding {false,false} →
  // live-full: two banners, two builders (NEXT ROUND eyebrows in-card),
  // full strip.
  const riding = {
    left: resolveZoneRiding(myBet.left, submittedStakes.left),
    right: resolveZoneRiding(myBet.right, submittedStakes.right)
  }
  const mode = resolvePanelMode(isBettingOpen, riding)

  // ── Bust "thump" cue (loss counterpart to the per-zone reward) ──────────
  // The win cue fires per zone at the cashout (CrashBetZone: playEffect
  // 'reward'). The LOSS counterpart is fired ONCE here at the panel, on the
  // useVisualCrash rising edge (false→true), IF the player had at least one
  // zone riding uncashed into the crash (a submitted, not-cashed-out bet that
  // becomes 'lost'). `riding` is the panel's own per-zone riding fact
  // (resolveZoneRiding): true while a non-terminal bet is on the wire, false
  // the instant a zone is cashed_out/won/cancelled — so a fully cashed-out or
  // never-placed player never hears the thump.
  //
  // We capture "was any zone riding" in a ref updated on every pre-crash
  // render, so the edge reads the PRE-crash riding state (not the post-crash
  // 'lost' state, which resolveZoneRiding also treats as terminal → not
  // riding). Keying on the single panel crash edge + one boolean guarantees
  // exactly ONE fire even when BOTH zones bust.
  const anyRiding = riding.left || riding.right
  const hadRidingBeforeCrashRef = useRef(false)
  if (!visualCrashed) hadRidingBeforeCrashRef.current = anyRiding
  const prevVisualCrashedRef = useRef(visualCrashed)
  useEffect(() => {
    const play = shouldPlayBustThump({
      visualCrashed,
      prevVisualCrashed: prevVisualCrashedRef.current,
      hadRidingBet: hadRidingBeforeCrashRef.current
    })
    prevVisualCrashedRef.current = visualCrashed
    if (play) playCrashBust()
  }, [visualCrashed, playCrashBust])

  // ── Rising-tension cue (owner ask) ──────────────────────────────────────
  // Sounds from ~2x until the crash, where the bust thump takes over.
  //
  // Polled at 200ms rather than per-frame: at 2x the multiplier climbs about
  // 0.077x/second, so the worst-case trigger error is ~0.015x — invisible, and
  // the owner confirmed "around 2x" is fine. setCrashCountdown is idempotent,
  // so a repeat poll with an unchanged answer publishes no store update.
  //
  // Reads the DISPLAY multiplier and the VISUAL crash, so the cue is on the
  // same clock as the number the player is watching — not the event clock,
  // which leads the video.
  useEffect(() => {
    // When the cue started sounding, so it can be capped. The cue LOOPS, so it
    // is the one sound that can outlive its round when a crash event never
    // arrives — every other condition would keep holding.
    let startedAt: number | null = null

    const tick = () => {
      const reading = getDisplayMultiplier()
      const active = resolveCountdownCue({
        multiplier: reading?.value ?? null,
        ageMs: reading?.ageMs ?? Number.POSITIVE_INFINITY,
        visualCrashed,
        isLive: !visualCrashed && Boolean(beaconStartedEvent),
        playingForMs: startedAt === null ? 0 : Date.now() - startedAt,
        // Dead beacon → the published clock is the event-clock fallback,
        // which LEADS the frames — hold the cue rather than spoil the rise.
        displayDegraded: isBeaconDead(getBeaconTime())
      })
      // Set once and never cleared here: the cap is per ROUND, and this effect
      // is re-created on every round boundary (visualCrashed / the new start
      // event are both deps), which is what resets it. Clearing it whenever the
      // cue falls silent would let a capped cue restart on the next tick.
      if (active && startedAt === null) startedAt = Date.now()
      // Swell with the round: quiet on entry at 2x, full by 5x. Mapped on
      // log(multiplier) so the rise is even by the CLOCK — the curve is
      // exponential in time, so a raw-multiplier ramp would crawl then lurch.
      // Phone speakers have no headroom and poor low-level reproduction, and
      // are already carrying the livestream's own music bed — the desktop
      // level (peaking at ~0.136 absolute) is inaudible there.
      soundActions.setCrashCountdown(
        active,
        resolveCountdownGain(
          reading?.value ?? 0,
          isMobile || isTablet ? MOBILE_GAIN_PROFILE : DESKTOP_GAIN_PROFILE
        )
      )
    }
    tick()
    const timer = window.setInterval(tick, 200)

    return () => {
      window.clearInterval(timer)
      // Leaving the panel (round over, game switch, interface hidden) must
      // silence a LOOPING cue — it would otherwise outlive its round.
      soundActions.setCrashCountdown(false)
    }
  }, [visualCrashed, beaconStartedEvent, soundActions])

  // v12.1 (tweak 3): zone GHOSTING is gone — a zone with no bet this round
  // stays visible and buildable through the live round (the "Tap to bet"
  // builder queues next-round stakes exactly like the post-cashout one).

  // ── Win banners (v12, ask 4) — DERIVED, never latched ──────────────────
  // A pure function of the store bet (selected through the hook's
  // myBetIdRef) + the submission-time cashout snapshot: appears on the
  // status flip to cashed, persists through race_started / visual crash /
  // race_ended by construction, and dies at race_ready because the round
  // reset clears myBetIdRef. Money = backend bet fields only (R3).
  const banners = {
    left: resolveZoneWinBanner(myBet.left, submittedCashout.left),
    right: resolveZoneWinBanner(myBet.right, submittedCashout.right)
  }
  const anyBanner = banners.left !== null || banners.right !== null
  // v12.1 (tweak 1): the could-have-won tease is GONE — the banner states
  // the banked outcome only ("keep it simple", owner).

  // ── Strip resting (v12.1) ──────────────────────────────────────────────
  // Betting closed ∧ no buildable zone (every card riding or busted). The
  // per-zone card state feeds it; hasEverClimbed only disambiguates
  // non-buildable states (live/locked/busted), so a constant is safe here.
  const zoneCardStates = (['left', 'right'] as Zone[]).map((zone) =>
    resolveZoneCardState({
      isBettingOpen,
      stake: stakes[zone],
      submittedStake: submittedStakes[zone],
      betStatus: myBet[zone]?.status,
      hasStartedClimbing,
      hasCrashed,
      visualCrashed,
      hasEverClimbed: true,
      betUnaccepted: myBetUnaccepted[zone],
      optimisticCashout: optCashout[zone] !== null
    })
  )

  // ── Chip strip visibility ──────────────────────────────────────────────
  // ALWAYS mounted (v6): while it RESTS the strip is non-interactive at a
  // readable ghost opacity (v7: "present, resting", not gone) and keeps its
  // layout space, so the zone cards above it never move (the panel is
  // bottom-anchored; unmounting the strip would drop the cards down).
  // v12.1: the strip WAKES whenever any zone is a builder — a freed cashed
  // zone, a queued stake, or the always-open empty sibling (tweak 3).
  const bottomHidden = resolveStripResting(isBettingOpen, zoneCardStates)

  // v15 (ASK 3): the DISPLAY precision for every rendered amount (stake,
  // riding value, banner, busted) — the currency's `displayPrecision`, so
  // crash matches every other chip-placement game's payout/balance pills
  // (fiat/stablecoins → 2, BTC/TRX → 8). Previously this used the WIRE
  // precision (`getInputPrecision`, 4 for fiat), which over-decimalled fiat
  // wins ("57.8156"). The submit/rounding path in useCrashChipBetting keeps
  // its own `inputPrecision` — this is display only, the money path is
  // untouched.
  const displayPrecision = getCrashDisplayPrecision(currency)

  // Spawn a flying chip from the selected chip disc to the tapped zone, and
  // pulse that zone on landing.
  const flyToZone = useCallback(
    (zone: Zone, targetRect: DOMRect) => {
      const chipEl = chipStripRef.current?.querySelector('.crash-chip-strip__chip--active')
      const chipValue = chips[selectedChipIdx]
      if (chipEl && chipValue !== undefined) {
        const chipRect = chipEl.getBoundingClientRect()
        const style = chipStyleForIndex(selectedChipIdx)
        const flyId = `crash-fly-${Date.now()}-${zone}`
        setFlyingChips((prev) => [
          ...prev,
          {
            id: flyId,
            fromX: chipRect.left + chipRect.width / 2,
            fromY: chipRect.top + chipRect.height / 2,
            toX: targetRect.left + targetRect.width / 2,
            toY: targetRect.top + targetRect.height / 2,
            bg: style.bg,
            border: style.border,
            amount: chipValue
          }
        ])
        setTimeout(() => {
          setFlyingChips((prev) => prev.filter((c) => c.id !== flyId))
        }, FLYING_CHIP_LIFETIME_MS)
      }

      // Restart the receive pulse cleanly so rapid taps re-fire it.
      setReceivingZone(null)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setReceivingZone(zone))
      })
      if (receiveClearRef.current) clearTimeout(receiveClearRef.current)
      receiveClearRef.current = setTimeout(() => setReceivingZone(null), RECEIVE_PULSE_MS)
    },
    [chips, selectedChipIdx]
  )

  // v49 — the tap is voiced by what actually happened: a genuine double
  // plays the stake-add chip sound + fires the gold punch; a masked/inert
  // tap plays the refusal cue instead (double() already fired its reasoned
  // notice where one applies). Same honesty for Clear below.
  const handleDouble = useCallback(() => {
    if (double() !== 'changed') {
      playEffect('cancel')
      return
    }
    playEffect('chip')
    setDoublePunchAt(0)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setDoublePunchAt(Date.now()))
    })
    setTimeout(() => setDoublePunchAt(0), DOUBLE_PUNCH_MS)
  }, [double, playEffect])

  // Trash = the removal sound the ✕ and reference panel use ('cancel').
  const handleClear = useCallback(() => {
    if (clear() === 'changed') playEffect('cancel')
  }, [clear, playEffect])

  // A previous round exists to repeat once a real stake snapshot was
  // captured (staked-then-cleared, or a submitted round). v47: gated on the
  // hook's hasRepeatable fact — a fresh session used to render an ENABLED
  // Repeat whose taps did literally nothing; now the strip shows the quiet
  // disabled ×2 until there is genuinely something to repeat.
  const canRepeat = totalStaked === 0 && hasRepeatable

  // Total submitted and still riding in the current round — the stats
  // overlay's "In play" figure while the round is live. Summed then rounded
  // at the currency precision (totalInPlay) so IEEE-754 dust can't eat a
  // cent off the pill's floor-based display.
  const inPlay = totalInPlay(
    (['left', 'right'] as Zone[]).map((z) => myBet[z]),
    inputPrecision
  )

  // v34 — "about to lock" warning: in the final betting seconds the
  // cash-out instruments warm amber (the lamp's own 3s threshold) so a
  // player mid-adjust sees the lock coming instead of discovering it after.
  // 500ms tick, betting only.
  const [closingSoon, setClosingSoon] = useState(false)
  useEffect(() => {
    if (!isBettingOpen) {
      setClosingSoon(false)
      return undefined
    }
    const tick = () => {
      // A hard round warms toward its board's lock, not the stamp's close.
      const hardClock = hardCloseClock(game.races[0], game.races[1])
      const info = getRaceCountdownInfo({
        hard: hardClock ? hardCountdown(hardClock, perfNow()) : null,
        raceEvents: game.races[0]?.raceEvents ?? [],
        nowMs: Date.now(),
        bettingWindowSec: getBettingTimeWindow(game),
        settledWindowSec: getSettledTimeWindow(game)
      })
      setClosingSoon(typeof info.current === 'number' && info.current >= 0 && info.current <= 3)
    }
    tick()
    const id = setInterval(tick, 500)
    return () => clearInterval(id)
  }, [isBettingOpen, game])

  // The potential-win readout (note c). `visualCrashed` gates the settle so
  // the pill can never resolve before the bust is on the viewer's frames;
  // `hasCrashed` (event clock) arms the crash-hold — without it the lost
  // status flip + the synthetic race_crashed would settle the readout at
  // event time and the visual gate would be dead code (review CRITICAL).
  const potential = useMemo(() => {
    const readout = resolvePotentialWin(
      [myBet.left, myBet.right],
      hasStartedClimbing,
      visualCrashed,
      hasCrashed
    )
    if (readout.kind !== 'idle') return readout
    // v49 (Aviator parity): nothing riding/banked — show the staged stakes'
    // would-be win (stake × auto-cashout target) instead of a dead dash.
    // Covers the betting window AND queued next-round stakes during live.
    // Clamped like the riding projection: an auto-cash-out target of 100x on a
    // max stake previews a figure far past the operator liability ceiling, and
    // the backend would pay the ceiling. Promising it at bet time is the same
    // lie as counting past it live, just earlier.
    const preview = resolveBettingPreviewWin(stakes, zoneAuto, payoutCeiling)
    return preview > 0 ? ({ kind: 'preview', amount: preview } as const) : readout
  }, [myBet, hasStartedClimbing, visualCrashed, hasCrashed, stakes, zoneAuto, payoutCeiling])

  // Report the stats up to the page's bottom-left overlay (Balance · Total
  // staked while betting/queueing, Balance · In play during a live round,
  // plus the potential-win pill).
  useEffect(() => {
    onStatsChange?.({ totalStaked, inPlay, potential })
  }, [totalStaked, inPlay, potential, onStatsChange])

  if (!betConfig) return null

  // ONE panel footprint across all modes (v6): live-collapsed no longer
  // narrows the column — geometry changes are what the state thrash was.
  const panelClass = 'crash-chip-panel'

  return (
    <div className={panelClass}>
      {/* v12.1 (tweak 2): the floating NEXT ROUND pill is GONE — the label
          lives IN-CARD, per zone (CrashBetZone renders the amber NEXT ROUND
          eyebrow on every builder card while betting is closed), so mixed
          states label exactly the right card. */}

      {/* Transient notice pill (v12.4 ask 7) — the reference chip-panel
          notice contract: single slot, replace-not-stack, re-keyed per fire
          so duplicates re-animate, role="status" polite. Non-interactive;
          in-flow above the banners so the bottom-anchored column grows
          upward and the zones never move. */}
      {noticeDisplay && (
        <div className="crash-chip-panel__notice-row">
          <div
            key={noticeDisplay.key}
            className={`crash-chip-panel__notice crash-chip-panel__notice--${noticeDisplay.tone}`}
            role="status"
            aria-live="polite"
          >
            {noticeDisplay.text}
          </div>
        </div>
      )}

      {/* Win banners (v12, ask 4) — an IN-FLOW row mirroring the zones
          (one cell per zone). The panel is bottom-anchored, so mounting
          this row grows the column UPWARD: cards + strip keep their exact
          screen position. */}
      {anyBanner && (
        <div className="crash-chip-panel__banners">
          {(['left', 'right'] as Zone[]).map((zone) => (
            <div key={zone} className="crash-chip-panel__banner-cell">
              {banners[zone] && (
                <CrashZoneWinBanner
                  banner={banners[zone]!}
                  currency={currency}
                  inputPrecision={displayPrecision}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Zones — both ALWAYS render AND stay interactive (v12.1 tweak 3:
          no ghosting — a zone with no bet this round is a live next-round
          builder; fixed geometry as ever). */}
      <div className="crash-chip-panel__zones">
        {(['left', 'right'] as Zone[]).map((zone) => {
          return (
            <div key={zone} className="crash-zone-slot">
              {/* v12 (ask 2): numbered identical slots — LEFT/RIGHT read as
                  two different markets; BET 1 / BET 2 cannot. */}
              <CrashBetZone
                zone={zone}
                label={zone === 'left' ? t('Bet 1') : t('Bet 2')}
                stake={stakes[zone]}
                submittedStake={submittedStakes[zone]}
                submittedCashout={submittedCashout[zone]}
                myBet={myBet[zone]}
                betUnaccepted={myBetUnaccepted[zone]}
                liveMultiplier={liveMultiplier}
                payoutCeiling={payoutCeiling}
                hasStartedClimbing={hasStartedClimbing}
                mode={mode}
                cashoutLocked={isZoneCashoutLocked(riding[zone])}
                closingSoon={closingSoon}
                onLockedCashoutTap={() => fireNotice('cashout-locked-round')}
                onNextRoundCashoutToggle={(off) =>
                  fireNotice(off ? 'cashout-off-next-round' : 'cashout-on-next-round')
                }
                hasCrashed={hasCrashed}
                visualCrashed={visualCrashed}
                crashedMultiplier={crashedMultiplier}
                currency={currency}
                inputPrecision={displayPrecision}
                auto={zoneAuto[zone]}
                cashingOut={cashingOut[zone]}
                optCashout={optCashout[zone]}
                isReceiving={receivingZone === zone}
                bannerActive={banners[zone] !== null}
                chips={chips}
                onPlaceChip={placeChip}
                onCashout={cashout}
                onSetAuto={(patch) => setZoneAuto(zone, patch)}
                onClearZone={clearZone}
                onFlyTarget={(rect) => flyToZone(zone, rect)}
              />
            </div>
          )
        })}
      </div>

      {/* Chip strip — ALWAYS mounted; rests in place (space reserved,
          readable ghost, non-interactive) during live-collapsed so the zone
          cards above it stay put. Chips remain immediately usable through
          betting AND live-full. */}
      <div className={`crash-dock__bottom ${bottomHidden ? 'is-hidden' : ''}`}>
        <div ref={chipStripRef}>
          <CrashChipStrip
            chips={chips}
            selectedChipIdx={selectedChipIdx}
            onSelect={selectChip}
            onClear={handleClear}
            onDouble={handleDouble}
            onRepeat={repeat}
            canControl={canControl}
            canRepeat={canRepeat}
            disabled={bottomHidden}
            doublePunchAt={doublePunchAt}
          />
        </div>
      </div>

      {/* Flying-chip animations (portaled to body) */}
      {typeof document !== 'undefined' &&
        flyingChips.length > 0 &&
        createPortal(
          <>
            {flyingChips.map((fly) => (
              <div
                key={fly.id}
                className="crash-flying-chip"
                style={
                  {
                    '--fly-from-x': `${fly.fromX}px`,
                    '--fly-from-y': `${fly.fromY}px`,
                    '--fly-to-x': `${fly.toX}px`,
                    '--fly-to-y': `${fly.toY}px`,
                    background: fly.bg,
                    borderColor: fly.border
                  } as React.CSSProperties
                }
              >
                <span>{formatNumberWithSuffix(fly.amount, t)}</span>
              </div>
            ))}
          </>,
          document.body
        )}
    </div>
  )
}
